import api from './api';
import { Product, ProductFormData } from '@/types/product';

const normalizeProductResponse = (payload: any): Product => payload?.product || payload;

export interface ProductListParams {
    search?: string;
    status?: 'active' | 'inactive';
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    outOfStock?: boolean;
    featured?: boolean;
    sortBy?: 'createdAt' | 'basePrice' | 'stock' | 'featuredOrder';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
}

export interface ProductListResponse {
    products: Product[];
    total: number;
    hasMore: boolean;
}

export interface ProductStats {
    total: number;
    active: number;
    sameDayDelivery: number;
    featured: number;
}

export interface ScrapedProductData {
    title: string;
    description: string;
    basePrice: number;
    campaignPrice?: number;
    images: string[];
    tags?: string[];
    sourceUrl: string;
    sku?: string;
    brand?: string;
    currency?: string;
}

export const productApi = {
    // Ürünleri listele (admin) — params verilmezse filtrelenmemiş TAM liste döner (limit yoksa sayfalama yapılmaz)
    async getAllProducts(params: ProductListParams = {}): Promise<ProductListResponse> {
        try {
            const response = await api.get('/admin/products', { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching products:', error);
            throw new Error(error.response?.data?.error || 'Ürünler yüklenemedi');
        }
    },

    // Ürün liste ekranı istatistik kartları (tam katalog taramadan, count() ile)
    async getStats(): Promise<ProductStats> {
        try {
            const response = await api.get('/admin/products/stats');
            return response.data;
        } catch (error: any) {
            console.error('Error fetching product stats:', error);
            throw new Error(error.response?.data?.error || 'İstatistikler yüklenemedi');
        }
    },

    // Toplu aktif/pasif yapma
    async bulkUpdateStatus(productIds: string[], status: 'active' | 'inactive'): Promise<{ message: string; updatedCount: number }> {
        try {
            const response = await api.post('/admin/products/bulk-status', { productIds, status });
            return response.data;
        } catch (error: any) {
            console.error('Error bulk updating status:', error);
            throw new Error(error.response?.data?.error || 'Toplu durum güncelleme başarısız');
        }
    },

    // Ürün detayı
    async getProduct(productId: string): Promise<Product> {
        try {
            const response = await api.get(`/admin/products/${productId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching product:', error);
            throw new Error(error.response?.data?.error || 'Ürün yüklenemedi');
        }
    },

    // Yeni ürün oluştur (admin)
    async createProduct(productData: ProductFormData): Promise<Product> {
        try {
            const response = await api.post('/admin/products', productData);
            return normalizeProductResponse(response.data);
        } catch (error: any) {
            console.error('Error creating product:', error);
            throw new Error(error.response?.data?.error || 'Ürün oluşturulamadı');
        }
    },

    // Ürün güncelle (admin)
    async updateProduct(productId: string, productData: Partial<ProductFormData>): Promise<Product> {
        try {
            const response = await api.put(`/admin/products/${productId}`, productData);
            return normalizeProductResponse(response.data);
        } catch (error: any) {
            console.error('Error updating product:', error);
            throw new Error(error.response?.data?.error || 'Ürün güncellenemedi');
        }
    },

    // Ürün sil (admin)
    async deleteProduct(productId: string): Promise<void> {
        try {
            await api.delete(`/admin/products/${productId}`);
        } catch (error: any) {
            console.error('Error deleting product:', error);
            throw new Error(error.response?.data?.error || 'Ürün silinemedi');
        }
    },

    // Vendor'a ürün ata
    async assignVendor(productId: string, vendorId: string): Promise<Product> {
        try {
            const response = await api.post(`/admin/products/${productId}/assign-vendor`, { vendorId });
            return response.data.product;
        } catch (error: any) {
            console.error('Error assigning vendor:', error);
            throw new Error(error.response?.data?.error || 'Vendor ataması başarısız');
        }
    },

    // Vendor'dan ürün kaldır
    async removeVendor(productId: string, vendorId: string): Promise<Product> {
        try {
            const response = await api.post(`/admin/products/${productId}/remove-vendor`, { vendorId });
            return response.data.product;
        } catch (error: any) {
            console.error('Error removing vendor:', error);
            throw new Error(error.response?.data?.error || 'Vendor kaldırma başarısız');
        }
    },

    // Vendor'ın ürünleri
    async getVendorProducts(vendorId: string): Promise<Product[]> {
        try {
            const response = await api.get(`/admin/products/vendor/${vendorId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching vendor products:', error);
            throw new Error(error.response?.data?.error || 'Vendor ürünleri yüklenemedi');
        }
    },

    // Ürün durumu güncelle
    async updateStatus(productId: string, status: 'active' | 'inactive'): Promise<Product> {
        try {
            const response = await api.put(`/admin/products/${productId}/status`, { status });
            return normalizeProductResponse(response.data);
        } catch (error: any) {
            console.error('Error updating status:', error);
            throw new Error(error.response?.data?.error || 'Durum güncellenemedi');
        }
    },

    // Toplu fiyat güncelleme
    async bulkPriceUpdate(params: {
        productIds?: string[];
        categoryId?: string;
        operation: 'percentage_increase' | 'percentage_decrease' | 'fixed_increase' | 'fixed_decrease' | 'set_price';
        value: number;
        target: 'basePrice' | 'campaignPrice';
    }): Promise<{
        message: string;
        updatedCount: number;
        results: { id: string; title: string; oldPrice: number; newPrice: number }[];
    }> {
        try {
            const response = await api.post('/admin/products/bulk-price-update', params);
            return response.data;
        } catch (error: any) {
            console.error('Error bulk updating prices:', error);
            throw new Error(error.response?.data?.error || 'Toplu fiyat güncelleme başarısız');
        }
    },

    // URL'den veya HTML kaynağından ürün bilgisi ve görsellerini çek
    async importProductFromUrl(url: string, uploadImages: boolean = true, html?: string): Promise<ScrapedProductData> {
        try {
            const response = await api.post('/admin/products/import-from-url', {
                url,
                uploadImages,
                html,
            });
            return response.data.product;
        } catch (error: any) {
            console.error('Error importing product from URL:', error);
            throw new Error(error.response?.data?.error || 'Ürün bilgileri çekilemedi');
        }
    },

    // Öne çıkan ürünlerin sıralamasını toplu kaydet
    async reorderFeaturedProducts(orderedIds: string[]): Promise<{ message: string; updatedCount: number }> {
        try {
            const response = await api.post('/admin/products/reorder-featured', { orderedIds });
            return response.data;
        } catch (error: any) {
            console.error('Error reordering featured products:', error);
            throw new Error(error.response?.data?.error || 'Öne çıkan ürün sıralaması güncellenemedi');
        }
    },
};
